import { ref, computed } from 'vue';
import quoteLabelService from '@/service/quoteLabelService';

// 전역 상태로 라벨 목록 관리 (모든 컴포넌트에서 공유)
const labels = ref([]);
const quoteLabelCache = ref({}); // { [creditQuoteId]: [labelIds] }
const isLabelsLoaded = ref(false);

export function useQuoteLabel() {
  const loading = ref(false);
  const error = ref(null);

  /**
   * 라벨 목록 조회 (캐싱)
   */
  const fetchLabels = async (force = false) => {
    if (isLabelsLoaded.value && !force) {
      return labels.value;
    }

    try {
      loading.value = true;
      error.value = null;
      const data = await quoteLabelService.getMyLabels();
      labels.value = data || [];
      isLabelsLoaded.value = true;
      return labels.value;
    } catch (err) {
      error.value = err;
      console.error('라벨 목록 조회 실패:', err);
      return [];
    } finally {
      loading.value = false;
    }
  };

  /**
   * 라벨 생성
   */
  const createLabel = async (name, colorCode) => {
    try {
      loading.value = true;
      error.value = null;
      const newLabel = await quoteLabelService.createLabel(name, colorCode);
      labels.value.push(newLabel);
      return newLabel;
    } catch (err) {
      error.value = err;
      console.error('라벨 생성 실패:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * 라벨 수정
   */
  const updateLabel = async (labelId, name, colorCode) => {
    try {
      loading.value = true;
      error.value = null;
      const updatedLabel = await quoteLabelService.updateLabel(labelId, name, colorCode);
      const index = labels.value.findIndex(l => l.id === labelId);
      if (index !== -1) {
        labels.value[index] = updatedLabel;
      }
      return updatedLabel;
    } catch (err) {
      error.value = err;
      console.error('라벨 수정 실패:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * 라벨 삭제
   */
  const deleteLabel = async (labelId) => {
    try {
      loading.value = true;
      error.value = null;
      await quoteLabelService.deleteLabel(labelId);
      labels.value = labels.value.filter(l => l.id !== labelId);
      
      // 캐시에서도 해당 라벨 제거
      Object.keys(quoteLabelCache.value).forEach(quoteId => {
        quoteLabelCache.value[quoteId] = quoteLabelCache.value[quoteId].filter(
          id => id !== labelId
        );
      });
    } catch (err) {
      error.value = err;
      console.error('라벨 삭제 실패:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * 호가에 라벨 추가
   */
  const addLabelToQuote = async (labelId, creditQuoteId) => {
    try {
      // 낙관적 업데이트
      if (!quoteLabelCache.value[creditQuoteId]) {
        quoteLabelCache.value[creditQuoteId] = [];
      }
      if (!quoteLabelCache.value[creditQuoteId].includes(labelId)) {
        quoteLabelCache.value[creditQuoteId].push(labelId);
      }

      await quoteLabelService.addLabelToQuote(labelId, creditQuoteId);
    } catch (err) {
      // 실패 시 롤백
      if (quoteLabelCache.value[creditQuoteId]) {
        quoteLabelCache.value[creditQuoteId] = quoteLabelCache.value[creditQuoteId].filter(
          id => id !== labelId
        );
      }
      console.error('라벨 추가 실패:', err);
      throw err;
    }
  };

  /**
   * 호가에서 라벨 제거
   */
  const removeLabelFromQuote = async (labelId, creditQuoteId) => {
    // 낙관적 업데이트를 위한 원본 저장
    const originalLabels = quoteLabelCache.value[creditQuoteId] || [];
    
    try {
      // 낙관적 업데이트
      if (quoteLabelCache.value[creditQuoteId]) {
        quoteLabelCache.value[creditQuoteId] = quoteLabelCache.value[creditQuoteId].filter(
          id => id !== labelId
        );
      }

      await quoteLabelService.removeLabelFromQuote(labelId, creditQuoteId);
    } catch (err) {
      // 실패 시 롤백
      quoteLabelCache.value[creditQuoteId] = originalLabels;
      console.error('라벨 제거 실패:', err);
      throw err;
    }
  };

  /**
   * 호가 데이터에서 라벨 ID 목록을 캐시에 저장
   * (API 호출 없이 호가 데이터의 labelIds 활용)
   */
  const setQuoteLabelIds = (creditQuoteId, labelIds) => {
    quoteLabelCache.value[creditQuoteId] = labelIds || [];
  };

  /**
   * 호가의 라벨 ID 목록 조회 (캐시에서)
   */
  const getLabelsForQuote = (creditQuoteId) => {
    return quoteLabelCache.value[creditQuoteId] || [];
  };

  /**
   * 호가에 라벨 토글 (있으면 제거, 없으면 추가)
   */
  const toggleLabelForQuote = async (labelId, creditQuoteId) => {
    const currentLabels = quoteLabelCache.value[creditQuoteId] || [];
    const hasLabel = currentLabels.includes(labelId);

    if (hasLabel) {
      await removeLabelFromQuote(labelId, creditQuoteId);
    } else {
      await addLabelToQuote(labelId, creditQuoteId);
    }
  };

  /**
   * 특정 호가가 특정 라벨을 가지고 있는지 확인
   */
  const hasLabel = (creditQuoteId, labelId) => {
    return quoteLabelCache.value[creditQuoteId]?.includes(labelId) || false;
  };

  /**
   * 호가의 라벨 객체 배열 반환
   */
  const getQuoteLabels = (creditQuoteId) => {
    const labelIds = quoteLabelCache.value[creditQuoteId] || [];
    return labels.value.filter(label => labelIds.includes(label.id));
  };

  /**
   * 캐시 초기화
   */
  const clearCache = () => {
    quoteLabelCache.value = {};
  };

  return {
    // 상태
    labels: computed(() => labels.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    isLabelsLoaded: computed(() => isLabelsLoaded.value),

    // 메서드
    fetchLabels,
    createLabel,
    updateLabel,
    deleteLabel,
    addLabelToQuote,
    removeLabelFromQuote,
    setQuoteLabelIds,
    getLabelsForQuote,
    toggleLabelForQuote,
    hasLabel,
    getQuoteLabels,
    clearCache,
  };
}

